import type {
  BatchArgs,
  BatchResponse,
  Entity,
  Folder,
  RecordingTypes,
  Sort,
  WorkspaceFolder,
} from '@carboncopyco/types';
import { Debug, removeUndefinedValues } from '@carboncopyco/utils';
import { flattenSearchResults, handleBatchRequest } from '../utils';
import { mainApi } from './mainApi';
import type { SearchResult } from './searchApi';

Debug.extend('demostack-data:api:foldersApi');

export interface CreateFolderArgs {
  organizationId: string;
  workspaceId: string;
  name: string;
}

export interface GetFolderArgs {
  organizationId: string;
  folderId: Folder['id'];
  removeDeleted?: boolean;
  workspaceId: string;
}

export interface UpdateFolderArgs extends GetFolderArgs {
  name: string;
}
export type UpdateFolderTagsArgs = {
  organizationId: string;
  workspaceId: string;
  folderId: Folder['id'];
  sharedWorkspaces: Folder['sharedWorkspaces'];
  tags: Folder['tags'];
};

export interface DeleteFolderArgs {
  organizationId: string;
  folderId: Folder['id'];
  workspaceId: string;
}

export interface RestoreFolderArgs {
  organizationId: string;
  folderId: Folder['id'];
  workspaceId: string;
}

export type GetFoldersArgs = {
  sort?: Sort;
  organizationId: string;
  entities?: Entity[];
  creators?: string[];
  recordingType?: RecordingTypes[];
  query?: string;
  limit?: number;
  workspaces?: string[];
  includeShared?: boolean;
};

export type GetWorkspaceFoldersArgs = Omit<GetFoldersArgs, 'workspaces'> & {
  workspaceId: string;
};

export interface GetFolderAssetsArgs extends BatchArgs<Sort> {
  workspaceId: string;
  organizationId: string;
  entities?: Entity[];
  creators?: string[];
  query?: string;
  deletedEntities?: boolean | 'include_all';
  recordingType?: RecordingTypes[];
  includeArchivedWorkspaces?: boolean;
  limit?: number;
  folderId?: Folder['id'];
}

export const folderApi = mainApi.injectEndpoints({
  endpoints: (builder) => ({
    getFolder: builder.query<Folder, GetFolderArgs>({
      query: ({
        organizationId,
        workspaceId,
        folderId,
        removeDeleted = true,
      }) => ({
        url: `/api/${organizationId}/folder/${folderId}`,
        method: 'GET',
        params: { removeDeleted, workspaceId },
      }),
      providesTags: (_, __, { folderId }) => [
        { type: 'Folder', id: String(folderId) },
      ],
    }),
    getFolders: builder.query<WorkspaceFolder[], GetFoldersArgs>({
      query: ({
        organizationId,
        entities,
        creators,
        recordingType,
        query,
        workspaces,
        sort,
        limit = 100,
        includeShared,
      }) => ({
        url: `/api/${organizationId}/folder/search`,
        method: 'GET',
        params: removeUndefinedValues({
          include_shared: includeShared,
          workspaces: workspaces?.join(','),
          limit,
          query,
          entities: entities?.join(','),
          creators: creators?.join(','),
          recordingType: recordingType?.join(','),
          sort,
        }),
      }),
      providesTags: (res) => [
        { type: 'Folders' },
        { type: 'Folders', id: res[0]?.workspaceId },
      ],
    }),
    getWorkspaceFolders: builder.query<
      WorkspaceFolder[],
      GetWorkspaceFoldersArgs
    >({
      query: ({
        organizationId,
        entities,
        creators,
        recordingType,
        query,
        workspaceId,
        sort,
        limit = 50,
      }) => ({
        url: `/api/${organizationId}/folder/search`,
        method: 'GET',
        params: removeUndefinedValues({
          includeShared: true,
          workspaces: workspaceId,
          limit,
          query,
          entities: entities?.join(','),
          creators: creators?.join(','),
          recordingType: recordingType?.join(','),
          deletedEntities: false,
          sort,
        }),
      }),
      providesTags: (_, __, { workspaceId }) => [
        { type: 'Folders', id: workspaceId },
      ],
    }),
    getArchivedWorkspaceFolders: builder.query<
      WorkspaceFolder[],
      GetWorkspaceFoldersArgs
    >({
      query: ({
        organizationId,
        entities,
        creators,
        recordingType,
        query,
        workspaceId,
        sort,
        limit = 100,
      }) => ({
        url: `/api/${organizationId}/folder/search`,
        method: 'GET',
        params: removeUndefinedValues({
          includeShared: true,
          workspaces: workspaceId,
          limit,
          query,
          entities: entities?.join(','),
          creators: creators?.join(','),
          recordingType: recordingType?.join(','),
          deletedEntities: true,
          sort,
        }),
      }),
      providesTags: (_, __, { workspaceId }) => [
        { type: 'ArchivedFolders', id: workspaceId },
      ],
    }),
    createFolder: builder.mutation<WorkspaceFolder, CreateFolderArgs>({
      query: ({ organizationId, workspaceId, name }) => {
        return {
          url: `/api/${organizationId}/folder/new`,
          method: 'PUT',
          body: {
            workspaceId,
            name,
          },
        };
      },
      invalidatesTags: (res, __, { workspaceId }) => [
        { type: 'Folders', id: workspaceId },
        { type: 'Folder', id: res.id },
      ],
    }),
    updateFolder: builder.mutation<Folder, UpdateFolderArgs>({
      query: ({ organizationId, folderId, name }) => {
        return {
          url: `/api/${organizationId}/folder/${folderId}`,
          method: 'POST',
          body: {
            name,
          },
        };
      },
      invalidatesTags: (_, __, { folderId, workspaceId }) => [
        { type: 'Folder', id: String(folderId) },
        { type: 'Folders', id: String(workspaceId) },
      ],
    }),
    updateFolderTags: builder.mutation<Folder, UpdateFolderTagsArgs>({
      query: ({ organizationId, folderId, tags, sharedWorkspaces }) => {
        return {
          url: `/api/${organizationId}/folder/${folderId}/tag`,
          method: 'PUT',
          body: {
            tags,
            sharedWorkspaces,
          },
        };
      },
      invalidatesTags: (_, __, { folderId, workspaceId }) => [
        { type: 'Folder', id: String(folderId) },
        { type: 'Folders', id: String(workspaceId) },
      ],
    }),
    deleteFolder: builder.mutation<Folder, DeleteFolderArgs>({
      query: ({ organizationId, folderId }) => {
        return {
          url: `/api/${organizationId}/folder/${folderId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (_, __, { folderId, workspaceId }) => [
        { type: 'Folder', id: folderId },
        { type: 'Folders', id: workspaceId },
        { type: 'ArchivedFolders', id: workspaceId },
      ],
    }),
    restoreFolder: builder.mutation<Folder, RestoreFolderArgs>({
      query: ({ organizationId, folderId }) => {
        return {
          url: `/api/${organizationId}/folder/${folderId}/restore`,
          method: 'POST',
        };
      },
      invalidatesTags: (_, __, { workspaceId, folderId }) => [
        { type: 'Folder', id: String(folderId) },
        { type: 'Folders', id: String(workspaceId) },
        {
          type: 'ArchivedFolders',
          id: String(workspaceId),
        },
      ],
    }),

    getFolderAssets: builder.query<
      BatchResponse<SearchResult>,
      GetFolderAssetsArgs
    >({
      query: ({
        organizationId,
        query,
        entities,
        workspaceId,
        folderId,
        sort,
        creators,
        deletedEntities,
        limit,
        recordingType,
        includeArchivedWorkspaces,
        ...rest
      }) => {
        return handleBatchRequest({
          ...rest,
          params: removeUndefinedValues(
            {
              sort,
              limit,
              workspaces: workspaceId,
              creators: creators?.join(','),
              query,
              entities: entities?.join(','),
              folderIds: folderId,
              deletedEntities,
              includeShared: true,
            },
            { keepEmptyStrings: true }
          ),
          url: `/api/${organizationId}/search`,
        });
      },
      serializeQueryArgs: ({ queryArgs }) => {
        if (!queryArgs) {
          return queryArgs;
        }
        const { nextBatchCursor, ...rest } = queryArgs;
        return rest;
      },
      merge: (queryCache, newBatch) => {
        queryCache.metadata = newBatch.metadata;
        const currentItemsIds = queryCache.items.map((item) => item.id);

        const newItems = newBatch.items.filter(
          (item) => !currentItemsIds.includes(item.id)
        );

        queryCache.items = [...queryCache.items, ...newItems];
      },
      forceRefetch({ currentArg, previousArg, endpointState }) {
        return (
          !(endpointState?.status === 'fulfilled') ||
          currentArg.nextBatchCursor !== previousArg.nextBatchCursor
        );
      },
      transformResponse: flattenSearchResults,
      providesTags: (_, __, { folderId }) => [
        { type: 'FolderAssets', id: folderId },
        { type: 'ArchivedFolderAssets', id: folderId },
      ],
    }),
  }),
});

export const {
  useCreateFolderMutation,
  useGetFolderQuery,
  useLazyGetFolderQuery,
  useUpdateFolderMutation,
  useDeleteFolderMutation,
  useRestoreFolderMutation,
  useGetWorkspaceFoldersQuery,
  useLazyGetWorkspaceFoldersQuery,
  useLazyGetArchivedWorkspaceFoldersQuery,
  useGetArchivedWorkspaceFoldersQuery,
  useGetFolderAssetsQuery,
  useLazyGetFolderAssetsQuery,
  useGetFoldersQuery,
  useLazyGetFoldersQuery,
  useUpdateFolderTagsMutation,
} = folderApi;
